import { fbt } from 'fbtee'
import { formatGenres } from '@shared/genres'
import { entryArtistIds, entryInterestIds } from '@shared/lineupActs'
import { Skeleton } from '../../../components/ui/skeleton'
import { InterestCard } from '../../../components/InterestCard'
import { FeedBadge } from '../../../components/FeedBadge'
import { SetNameContent } from '../../../components/SetNameContent'
import { SetDetailDialog, type SetDetailTab } from '../../../components/SetDetailDialog'
import { setImages } from '../../../lib/setImages'
import type { SetFeed } from '../../../hooks/useSetPosts'
import { useHistoryState } from '../../../hooks/useHistoryState'
import type { LineupEntry, Artist, UserProfile } from '../../../types'
import { LineupActDialog } from './LineupActDialog'
import { actCardLabel, actUserIds, isActFollowed, isActFollowHinted } from './lineupActCard'

const NO_GROUPS: never[] = []
const noop = () => {}

export function LineupTieredGrid({
  entries,
  artists,
  interestedArtistIds,
  onToggleArtist,
  onMarkArtists,
  followHintIds,
  onArtistNameClick,
  artistUserIds,
  userProfiles,
  currentUserId,
  feed,
  // Search results pass this to show day/stage context instead of genres, and
  // `flat` to render one uniform grid (a filtered subset reads cleaner uniform
  // than tier-sized).
  subtitleFor,
  flat = false,
}: {
  entries: LineupEntry[]
  artists: Record<string, Artist>
  interestedArtistIds: Set<string>
  onToggleArtist: (artistId: string) => void
  onMarkArtists: (artistIds: string[]) => void
  followHintIds: ReadonlySet<string>
  onArtistNameClick: (artistId: string) => void
  artistUserIds: Record<string, string[]>
  userProfiles: Record<string, UserProfile>
  currentUserId?: string | null
  feed?: SetFeed
  subtitleFor?: (entry: LineupEntry) => string | undefined
  flat?: boolean
}) {
  const hasTiers = !flat && entries.some(e => e.tier != null)
  // Several grids render at once (one per day/stage), so the open dialog is
  // keyed by lineup entry and only the grid holding that entry shows it.
  const [saved, setDetail] = useHistoryState<{ entryId: string; tab: SetDetailTab } | null>('lineupArtist', null)
  const detailEntry = saved ? entries.find(e => e.id === saved.entryId) : undefined
  const isAct = (entry: LineupEntry) => entryInterestIds(entry).length > 1

  function makeCard(entry: LineupEntry, square = false) {
    const ids = entryArtistIds(entry)
    const act = isAct(entry)
    const open = (tab: SetDetailTab) => setDetail({ entryId: entry.id, tab })
    const followed = isActFollowed(entry, interestedArtistIds)
    const { name, tagline, leadTitle } = actCardLabel(entry, artists)
    const images = setImages(entry, artists)
    const first = artists[ids[0]]
    // An act's heart only ever adds: a filled act heart opens the detail,
    // where each artist has its own.
    const toggle = !act ? () => onToggleArtist(ids[0]) : followed ? () => open('details') : () => onMarkArtists(ids)
    return (
      <InterestCard
        key={entry.id}
        historyKey={`lineup:${entry.id}`}
        imageUrl={images.imageUrl}
        imageFocal={images.focal}
        imageUrls={images.imageUrls}
        imageFocals={images.imageFocals}
        seed={images.seed}
        name={name}
        nameContent={
          ids.length > 1
            ? <SetNameContent artistIds={ids} title={leadTitle} artists={artists} compact={undefined} />
            : first ? undefined : <Skeleton className="h-4 w-20" />
        }
        tagline={tagline}
        subtitle={subtitleFor?.(entry) ?? entry.note ?? (act ? undefined : formatGenres(first?.genres) ?? undefined)}
        status={followed ? 'interested' : 'none'}
        followHint={!followed && isActFollowHinted(entry, followHintIds)}
        interestedMemberIds={actUserIds(entry, artistUserIds)}
        goingMemberIds={[]}
        userProfiles={userProfiles}
        currentUserId={currentUserId}
        onToggle={toggle}
        onNameClick={act ? () => open('details') : () => onArtistNameClick(ids[0])}
        onOpenDetail={act || feed ? open : undefined}
        badge={feed && !act ? (
          <FeedBadge
            summary={feed.summaryByArtist.get(ids[0])}
            ariaLabel={fbt('Open artist feed', 'Accessible label for the artist feed badge on a lineup card')}
            onOpen={() => open('feed')}
          />
        ) : undefined}
        className={square ? 'aspect-square' : undefined}
      />
    )
  }

  const closeDetail = () => setDetail(null)
  const changeTab = (tab: SetDetailTab) => saved && setDetail({ ...saved, tab })
  let detailDialog: React.ReactNode = null
  if (saved && detailEntry && isAct(detailEntry)) {
    detailDialog = (
      <LineupActDialog
        entry={detailEntry}
        tab={saved.tab}
        onTabChange={changeTab}
        onClose={closeDetail}
        artists={artists}
        interestedArtistIds={interestedArtistIds}
        followHintIds={followHintIds}
        onToggleArtist={onToggleArtist}
        artistUserIds={artistUserIds}
        userProfiles={userProfiles}
        currentUserId={currentUserId}
        groups={feed?.groups ?? NO_GROUPS}
        isGuest={feed?.isGuest ?? !currentUserId}
        onRequireAuth={feed?.onRequireAuth ?? noop}
      />
    )
  } else if (saved && detailEntry && feed) {
    const artistId = entryArtistIds(detailEntry)[0]
    detailDialog = (
      <SetDetailDialog
        open={true}
        onOpenChange={open => { if (!open) closeDetail() }}
        tab={saved.tab}
        onTabChange={changeTab}
        name={artists[artistId]?.name}
        artistId={artistId}
        interestedMemberIds={artistUserIds[artistId] ?? []}
        goingMemberIds={[]}
        currentUserStatus={interestedArtistIds.has(artistId) ? 'interested' : null}
        posts={feed.postsByArtist.get(artistId) ?? []}
        profiles={userProfiles}
        currentUserId={currentUserId}
        groups={feed.groups}
        isGuest={feed.isGuest}
        onRequireAuth={feed.onRequireAuth}
        onCreatePost={feed.createPost}
        onDeletePost={feed.deletePost}
      />
    )
  }

  if (!hasTiers) {
    return (
      <>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {entries.map(e => makeCard(e, true))}
        </div>
        {detailDialog}
      </>
    )
  }

  const tier1 = entries.filter(e => e.tier === 1)
  const tier2 = entries.filter(e => e.tier === 2)
  const tier3 = entries.filter(e => !e.tier || e.tier >= 3)

  return (
    <>
      <div className="space-y-4">
        {tier1.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {tier1.map(e => makeCard(e, true))}
          </div>
        )}
        {tier2.length > 0 && (
          <>
            {tier1.length > 0 && <div className="border-t border-border" />}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {tier2.map(e => makeCard(e, true))}
            </div>
          </>
        )}
        {tier3.length > 0 && (
          <>
            {(tier1.length > 0 || tier2.length > 0) && <div className="border-t border-border" />}
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {tier3.map(e => makeCard(e))}
            </div>
          </>
        )}
      </div>
      {detailDialog}
    </>
  )
}
